import React, { useState } from 'react';
import { 
  Paper, 
  Typography, 
  Box,
  IconButton,
  useTheme
} from '@mui/material';
import { 
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';

interface ImagePreviewProps {
  image: string;
  title: string;
}

const ImagePreview: React.FC<ImagePreviewProps> = ({ image, title }) => {
  const theme = useTheme();
  const [scale, setScale] = useState(1);

  const handleZoomIn = () => setScale(prev => Math.min(prev + 0.1, 2));
  const handleZoomOut = () => setScale(prev => Math.max(prev - 0.1, 0.5));

  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = image;
    link.download = `${title.toLowerCase()}_manhwa.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Paper
        elevation={3}
        sx={{
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          height: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <Typography variant="h6" color="text.primary">
            {title}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <IconButton 
              onClick={handleZoomOut}
              size="small"
              disabled={scale <= 0.5}
            >
              <ZoomOutIcon />
            </IconButton>
            <IconButton 
              onClick={handleZoomIn}
              size="small"
              disabled={scale >= 2}
            >
              <ZoomInIcon />
            </IconButton>
            <IconButton 
              onClick={handleDownload}
              size="small"
            >
              <DownloadIcon />
            </IconButton>
          </Box>
        </Box>

        <Box
          sx={{
            position: 'relative',
            overflow: 'hidden',
            borderRadius: 1,
            backgroundColor: 'background.default',
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box
            component="img"
            src={image}
            alt={title}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'contain',
              transform: `scale(${scale})`,
              transition: 'transform 0.2s ease-in-out',
            }}
          />
        </Box>
      </Paper>
    </motion.div>
  );
};

export default ImagePreview; 